import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import produce from 'immer';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import { IUserSessionComponentProps } from '../../../../../models/AppState';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getDisplayName } from '../../../../../models/NameUtils';
import { isTagSearchContactFilterCriteria, numberToFormattedCount } from '../../../../../models/UiUtils';
import { useAutomationTemplateIdQuery } from '../../../../../queries/AutomationTemplate';
import { useCustomFieldLabelMap } from '../../../../../queries/Contact/useContactCustomFields';
import { useAdvancedFiltersContext } from '../../../../components-v2/AdvancedFilters/AdvancedFiltersContext';
import {
	EntityPropertySearchTag,
	IEntityPropertySearch,
} from '../../../../components/entities/EntityPropertySearchTag';
import { ContactFilterRequestTagList } from '../../../../components/reporting/emails/GroupEmailTableRow/ContactFilterRequestTagList';
import { baseStyleSheet } from '../../../../styles/styles';
import { EOwnershipOption, useOwnershipFilters } from '../hooks/useOwnershipFilter';
import { EStatusOption, useStatusFilters } from '../hooks/useStatusFilter';
import { usePeopleSearch } from '../PeopleContext';
import { styleSheet } from './styles';

interface IProps extends Partial<IUserSessionComponentProps> {
	className?: string;
	styles?: StyleDeclarationValue[];
	filter: Api.IBulkContactsRequest;
	onChangeFilter: (filter: Api.IBulkContactsRequest) => void;
}

export const PeopleSearchDescription: React.FC<IProps> = observer(({ filter, className, styles, onChangeFilter }) => {
	const userSession = useUserSession();
	const { logApiError } = useEventLogging();
	const { contactsQuery } = usePeopleSearch();
	const { selectedOwnership } = useOwnershipFilters({
		filter,
		onChangeFilter,
	});
	const { selectedStatuses } = useStatusFilters({ filter, onChangeFilter });
	const { tagFilters } = useAdvancedFiltersContext();
	const [selectedUser, setSelectedUser] = React.useState('');
	const isTablet = useMediaQuery({ maxWidth: 1200 });
	const isWideScreen = useMediaQuery({ minWidth: 1400 });
	const maxTagsToShow = isWideScreen ? 4 : isTablet ? 2 : 3;

	// Get automation ID from filter criteria
	const automationId = React.useMemo(() => {
		const inProgressCriteria = filter.filter?.criteria?.find(
			criterion => criterion.property === Api.ContactFilterCriteriaProperty.InProgressAutomations
		);
		const completedCriteria = filter.filter?.criteria?.find(
			criterion => criterion.property === Api.ContactFilterCriteriaProperty.PreviousAutomations
		);
		return inProgressCriteria?.value || completedCriteria?.value || null;
	}, [filter]);

	// Use existing query hook to get automation template by ID
	const { data: automationTemplate } = useAutomationTemplateIdQuery({
		id: automationId || '',
		enabled: !!automationId,
	});

	const automationName = automationTemplate?.name || '';

	const { labelByName: customFieldLabelByName } = useCustomFieldLabelMap();

	React.useEffect(() => {
		const ownedBy = filter.ownershipFilter?.criteria;

		if (!ownedBy?.value || ownedBy.value === userSession.user.id) {
			setSelectedUser('');
			return;
		}

		const user = new Api.UserViewModel(userSession, { id: ownedBy.value });
		user
			.load()
			?.then(() => {
				setSelectedUser(getDisplayName(user));
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				logApiError('ContactsFetchUser-Error', err);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter]);

	const removeTag = (tag: IEntityPropertySearch) => {
		const nextFilter = produce(filter, draftFilter => {
			const criteriaWithNoTags = draftFilter.filter.criteria.filter(x => !isTagSearchContactFilterCriteria(x));

			const orTagCriteria = draftFilter.filter.criteria.find(x => x.op === Api.FilterOperator.Or);
			if (orTagCriteria?.criteria) {
				orTagCriteria.criteria = orTagCriteria?.criteria?.filter(x => x.value !== tag.value);
				// Remove empty OR criteria
				if (orTagCriteria.criteria.length === 0) {
					orTagCriteria.criteria = null;
				}
			}

			const andTagCriteria = draftFilter.filter.criteria.find(x => x.op === Api.FilterOperator.And);
			if (andTagCriteria?.criteria) {
				andTagCriteria.criteria = andTagCriteria.criteria.filter(x => x.value !== tag.value);
				// Remove empty AND criteria
				if (andTagCriteria.criteria.length === 0) {
					andTagCriteria.criteria = null;
				}
			}

			const notTagCriteria = draftFilter.filter.criteria
				.filter(x => x.op === Api.FilterOperator.Not)
				.filter(x => x.value !== tag.value);

			draftFilter.filter.criteria = [...criteriaWithNoTags];
			if (orTagCriteria?.criteria?.length) {
				draftFilter.filter.criteria.push(orTagCriteria);
			}
			if (andTagCriteria?.criteria?.length) {
				draftFilter.filter.criteria.push(andTagCriteria);
			}
			notTagCriteria.forEach(criterion => {
				draftFilter.filter.criteria.push(criterion);
			});
		});

		// Update the tag filters context before triggering the filter change
		tagFilters.refreshTags(nextFilter.filter.criteria);
		onChangeFilter(nextFilter);
	};

	// Calculate total results from all pages in react-query
	const totalResults = React.useMemo(() => {
		if (!contactsQuery.data?.pages?.length) return 0;
		return contactsQuery.data.pages[0].totalCount || 0;
	}, [contactsQuery.data?.pages]);

	const search =
		filter.filter?.criteria?.find(x => x.property === Api.ContactFilterCriteriaProperty.All)?.value ??
		filter.filter?.criteria?.find(x => x.property === Api.ContactFilterCriteriaProperty.Name)?.value ??
		filter.filter?.criteria?.find(x => x.property === Api.ContactFilterCriteriaProperty.Company)?.value ??
		'';

	const SelectedStatusToDescriptionMap = {
		[EStatusOption.WithEmail]: 'with an email',
		[EStatusOption.WithPhoneNumber]: 'with a phone number',
		[EStatusOption.WithAddress]: 'with an address',
		[EStatusOption.HaveAlertsDue]: 'with alerts due',
		[EStatusOption.PrivateContacts]: 'marked private',
		[EStatusOption.KeepInTouch]: 'due to keep in touch',
		[EStatusOption.WithoutTags]: 'without tags',
		[EStatusOption.NoEmail]: 'without email',
		[EStatusOption.WithoutPhoneNumber]: 'without phone number',
		[EStatusOption.NoAddress]: 'without address',
		[EStatusOption.Archived]: 'Archived',
	};

	const statusesDescription = selectedStatuses
		.map(status => {
			return SelectedStatusToDescriptionMap[status];
		})
		.join(', ');

	const getDateRageDescription = React.useCallback(
		(property: Api.ContactFilterCriteriaProperty, propertyName: string) => {
			const dateCriteria = filter.filter?.criteria?.find(criterion => {
				return criterion.criteria?.find(subCriterion => {
					return subCriterion.property === property;
				});
			});

			if (!dateCriteria) {
				return '';
			}

			const relativeDates = dateCriteria?.criteria.find(
				criterion => criterion.values && Object.keys(criterion.values).length > 0
			);

			if (dateCriteria?.criteria.find(c => c.value === 'never')) {
				return 'never contacted before';
			}
			if (relativeDates) {
				const years = relativeDates.values['years'];
				const yearsValue = -1 * parseInt(years);
				const months = relativeDates.values['months'];
				const monthsValue = -1 * parseInt(months);
				const days = relativeDates.values['days'];
				const daysValue = -1 * parseInt(days);

				if (years) {
					return `last contacted more than ${yearsValue} year${yearsValue > 1 ? 's' : ''} ago`;
				}
				if (months) {
					return `last contacted more than ${monthsValue} month${monthsValue > 1 ? 's' : ''} ago`;
				}
				if (days) {
					return `last contacted more than ${daysValue} day${daysValue > 1 ? 's' : ''} ago`;
				}
			}

			const startDate = moment(
				dateCriteria?.criteria.find(criterion => {
					return criterion.op === Api.FilterOperator.Gte;
				})?.value
			);
			const endDateCriteria = dateCriteria?.criteria.find(criterion => {
				return criterion.op === Api.FilterOperator.Lte;
			});

			let endDate: moment.Moment | undefined;
			if (endDateCriteria?.value) {
				endDate = moment(endDateCriteria?.value);
			}

			if (!startDate && !endDate) {
				return '';
			} else if (!endDate) {
				return `with a ${propertyName} after ${startDate.format('MMMM Do, YYYY')}`;
			}

			return `with a ${propertyName} between ${startDate.format('MMMM Do, YYYY')} and ${endDate.format('MMMM Do, YYYY')}`;
		},
		[filter]
	);

	const creationDateDescription = React.useMemo(
		() => getDateRageDescription(Api.ContactFilterCriteriaProperty.CreationDate, 'creation date'),
		[getDateRageDescription]
	);

	const lastCommunicationDateDescription = React.useMemo(
		() => getDateRageDescription(Api.ContactFilterCriteriaProperty.LastCommunicationDate, 'last communication date'),
		[getDateRageDescription]
	);

	const keyFactDescription = React.useMemo(() => {
		const keyFactCriteria = filter.filter?.criteria?.find(criterion => {
			return criterion.criteria?.find(subCriterion => {
				return subCriterion.property === Api.ContactFilterCriteriaProperty.KeyDateKind;
			});
		});
		if (!keyFactCriteria?.criteria) {
			return '';
		}

		const startDate = moment(
			keyFactCriteria.criteria.find(criterion => {
				return criterion.op === Api.FilterOperator.Gte;
			}).value
		);
		const endDate = moment(
			keyFactCriteria.criteria.find(criterion => {
				return criterion.op === Api.FilterOperator.Lte;
			}).value
		);
		const keyDateCriteria = keyFactCriteria.criteria.find(criterion => {
			return criterion.property === Api.ContactFilterCriteriaProperty.KeyDateKind;
		});
		if (keyDateCriteria?.value) {
			const isAYearOrMoreApart = endDate.diff(startDate, 'days') > 364;
			if (keyDateCriteria.value === Api.KeyDateKind.Birthday && isAYearOrMoreApart) {
				const guessAge = (date: moment.Moment) => {
					return moment().diff(date, 'years');
				};
				const estimatedDateOfFilterCreation = endDate.format('MMMM Do');
				const today = moment().format('MMMM Do');

				const asOfMessage =
					today === estimatedDateOfFilterCreation
						? 'as of today'
						: ` as of ${estimatedDateOfFilterCreation} (when filter was created)`;

				return `between the ages of ${guessAge(endDate)} and ${guessAge(startDate)} ${asOfMessage}`;
			}
			return `with a ${keyDateCriteria.value} between ${startDate.format('MMMM Do, YYYY')} and ${endDate.format(
				'MMMM Do, YYYY'
			)}`;
		}
		return '';
	}, [filter]);

	const locationDescription = React.useMemo(() => {
		// Find a location criterion either as a top-level leaf or inside an Or group
		// (the AI may send Or groups with variants like "NY" | "New York" for the same property)
		const findLocationCriteria = (property: Api.ContactFilterCriteriaProperty) => {
			const criteria = filter.filter?.criteria;
			if (!criteria) return undefined;

			// Direct leaf match
			const direct = criteria.find(c => c.property === property);
			if (direct) return direct;

			// Check inside Or groups — use the first child's value for display
			for (const c of criteria) {
				if (c.op === Api.FilterOperator.Or && c.criteria?.length && c.criteria[0]?.property === property) {
					return c.criteria[0];
				}
			}
			return undefined;
		};

		const cityCriteria = findLocationCriteria(Api.ContactFilterCriteriaProperty.City);
		const stateCriteria = findLocationCriteria(Api.ContactFilterCriteriaProperty.StateProvince);
		const zipCriteria = findLocationCriteria(Api.ContactFilterCriteriaProperty.PostalCode);

		const parts: string[] = [];

		if (cityCriteria?.value) {
			parts.push(cityCriteria.value);
		}
		if (stateCriteria?.value) {
			parts.push(stateCriteria.value);
		}
		if (zipCriteria?.value) {
			parts.push(zipCriteria.value);
		}

		if (parts.length === 0) {
			return '';
		}

		return `in ${parts.join(', ')}`;
	}, [filter]);

	const customFieldDescription = React.useMemo(() => {
		const customFieldCriteria = (filter.filter?.criteria ?? []).filter(
			c => c.property === Api.ContactFilterCriteriaProperty.CustomField && c.value
		);
		if (customFieldCriteria.length === 0) {
			return '';
		}
		return customFieldCriteria
			.map(c => {
				const fieldNameValue = c.values?.FieldName;
				const label =
					(fieldNameValue && customFieldLabelByName.get(fieldNameValue)) || fieldNameValue || 'custom field';
				return `with ${label} "${c.value}"`;
			})
			.join(' and ');
	}, [filter, customFieldLabelByName]);

	const companyTagDescription = React.useMemo(() => {
		const companyTagCriteria = filter.filter?.criteria?.find(
			criterion => criterion.property === Api.ContactFilterCriteriaProperty.CompanyTag
		);

		if (!companyTagCriteria?.value) {
			return '';
		}

		return `at companies tagged "${companyTagCriteria.value}"`;
	}, [filter]);

	const automationDescription = React.useMemo(() => {
		const inProgressCriteria = filter.filter?.criteria?.find(
			criterion => criterion.property === Api.ContactFilterCriteriaProperty.InProgressAutomations
		);
		const completedCriteria = filter.filter?.criteria?.find(
			criterion => criterion.property === Api.ContactFilterCriteriaProperty.PreviousAutomations
		);

		if (inProgressCriteria?.value) {
			if (automationName) {
				return `with "${automationName}" automation in progress`;
			}
			return `with automation in progress (ID: ${inProgressCriteria.value})`;
		}

		if (completedCriteria?.value) {
			if (automationName) {
				return `with "${automationName}" automation completed`;
			}
			return `with automation completed (ID: ${completedCriteria.value})`;
		}

		return '';
	}, [filter, automationName]);

	return (
		<div className={`${css(styleSheet.container, baseStyleSheet.truncateText, ...(styles || []))} ${className || ''}`}>
			<span className={css(baseStyleSheet.fontBold)}>{numberToFormattedCount(totalResults)}</span>
			&nbsp;
			{totalResults === 1 ? 'contact' : 'contacts'}
			&nbsp;
			{selectedOwnership === EOwnershipOption.Me && <span>I own&nbsp;</span>}
			{selectedOwnership === EOwnershipOption.Owned && selectedUser && (
				<span>
					{' '}
					owned by <span className={css(baseStyleSheet.fontBold)}>{selectedUser}</span>
					&nbsp;
				</span>
			)}
			{selectedOwnership === EOwnershipOption.Connections && <span>with connections&nbsp;</span>}
			{!!search && (
				<span>
					{' for "'}
					<span className={css(baseStyleSheet.fontBold)}>{search + '"'}</span>
					&nbsp;
				</span>
			)}
			{statusesDescription}
			{!!statusesDescription && <span>&nbsp;</span>}
			{locationDescription}
			{!!locationDescription && <span>&nbsp;</span>}
			{creationDateDescription}
			{!!creationDateDescription && <span>&nbsp;</span>}
			{lastCommunicationDateDescription}
			{!!lastCommunicationDateDescription && <span>&nbsp;</span>}
			{keyFactDescription}
			{!!keyFactDescription && <span>&nbsp;</span>}
			{automationDescription}
			{!!automationDescription && <span>&nbsp;</span>}
			{companyTagDescription}
			{!!companyTagDescription && <span>&nbsp;</span>}
			{customFieldDescription}
			{!!customFieldDescription && <span>&nbsp;</span>}
			<ContactFilterRequestTagList
				filterRequest={{
					criteria: filter.filter?.criteria,
					op: filter.filter?.op,
				}}
				maxTagsToDisplayInline={maxTagsToShow}
				renderTag={(tag, op, index) => {
					const s: IEntityPropertySearch = { value: tag };
					return (
						<React.Fragment key={`psd-tag-${tag}`}>
							{index === 0 ? 'with ' : ``}
							<span className={css(styleSheet.tag)}>
								{index > 0 && <span className={css(styleSheet.tagTokenOpPrefix)}>{op.toLowerCase()}</span>}
								<EntityPropertySearchTag
									className={css(styleSheet.filterPill)}
									op={op}
									search={s}
									onRemoveSearch={() => removeTag(s)}
								/>
							</span>
						</React.Fragment>
					);
				}}
				renderOverflowTag={(tag, op) => {
					const s: IEntityPropertySearch = { value: tag };
					return (
						<span className={css(styleSheet.extraTagsItem)} key={`psd-extra-tag-${tag}`}>
							<span className={css(styleSheet.tagTokenOpPrefix)}>{op.toLowerCase()}</span>
							<EntityPropertySearchTag
								className={css(styleSheet.filterPill)}
								op={op}
								search={s}
								onRemoveSearch={() => removeTag(s)}
							/>
						</span>
					);
				}}
			/>
		</div>
	);
});
